import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BusinessAuthProvider } from './context/BusinessAuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { Toaster } from './components/ui/sonner';
import FeedbackWidget from './components/FeedbackWidget';
import CookieBanner from './components/CookieBanner';
import { BusinessProtectedRoute } from './components/BusinessProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import WaitlistLandingPage from './pages/WaitlistLandingPage';
import AdminWaitlistPage from './pages/AdminWaitlistPage';
import UnifiedDashboardPage from './pages/UnifiedDashboardPage';
import JoinPage from './pages/JoinPage';
import BizSetupPage from './pages/BizSetupPage';
import BizInvoicesPage from './pages/BizInvoicesPage';
import BizSplitsPage from './pages/BizSplitsPage';
import BizPledgesPage, { PublicPledgePage } from './pages/BizPledgesPage';
import FeaturesPage from './pages/FeaturesPage';
import TrustSecurityPage from './pages/TrustSecurityPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import MyLibraryPage from './pages/MyLibraryPage';
import AboutUsPage from './pages/AboutUsPage';
import OurTeamPage from './pages/OurTeamPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForcedPasswordResetPage from './pages/ForcedPasswordResetPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PasscodeSetupPage from './pages/PasscodeSetupPage';
import PasscodeGate from './components/PasscodeGate';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import WorkspaceOverviewPage from './pages/WorkspaceOverviewPage';
import WorkspaceSectionPage from './pages/WorkspaceSectionPage';
import SendMoneyPage from './pages/SendMoneyPage';
import RequestMoneyPage from './pages/RequestMoneyPage';
import RequestsPage from './pages/RequestsPage';
import WalletPage from './pages/WalletPage';
import WalletTopupPage from './pages/WalletTopupPage';
import PhoneVerifyPage from './pages/PhoneVerifyPage';
import SettingsPage from './pages/SettingsPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import ProfilePage from './pages/ProfilePage';
import ProductFormPage from './pages/ProductFormPage';
import ExplorePage from './pages/ExplorePage';
import ProductDetailPage from './pages/ProductDetailPage';
import UserProfilePage from './pages/UserProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import FeedPage from './pages/FeedPage';
import AdminFeesPage from './pages/AdminFeesPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import SellerAnalyticsPage from './pages/SellerAnalyticsPage';
import CRMPage from './pages/CRMPage';
import BuyerMessagesPage from './pages/BuyerMessagesPage';
import AdminDomainsPage from './pages/AdminDomainsPage';
import AdminAPIPage from './pages/AdminAPIPage';
import StorefrontPage from './pages/StorefrontPage';
import PayLinkPage from './pages/PayLinkPage';
import EmbedWidgetPage from './pages/EmbedWidgetPage';
import ReceiptPage from './pages/ReceiptPage';
import WithdrawPage from './pages/WithdrawPage';
import AdminWithdrawalsPage from './pages/AdminWithdrawalsPage';
import TwoFAPage from './pages/TwoFAPage';
import AdminSecurityPage from './pages/AdminSecurityPage';
import PrivacySettingsPage from './pages/PrivacySettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import InviteFriendsPage from './pages/InviteFriendsPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import MySubscriptionsPage from './pages/MySubscriptionsPage';
import InvestorDashboardPage from './pages/InvestorDashboardPage';
import AdminFeaturesPage from './pages/AdminFeaturesPage';
import AdminBetaPage from './pages/AdminBetaPage';
import RoadmapPage from './pages/RoadmapPage';
import BackUsPage from './pages/BackUsPage';
import BusinessLoginPage from './pages/BusinessLoginPage';
import BusinessAuthCallback from './pages/BusinessAuthCallback';
import OrgSwitcherPage from './pages/OrgSwitcherPage';
import MfaEnrollPage from './pages/MfaEnrollPage';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Public Route Component (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in hash - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<WaitlistLandingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/trust" element={<TrustSecurityPage />} />
      <Route path="/security" element={<TrustSecurityPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/team" element={<OurTeamPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forced-password-reset" element={<ForcedPasswordResetPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/passcode/setup" element={
        <ProtectedRoute>
          <PasscodeSetupPage />
        </ProtectedRoute>
      } />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/u/:userId" element={<UserProfilePage />} />
      <Route path="/store/:username" element={<StorefrontPage />} />
      <Route path="/pay/:username" element={<PayLinkPage />} />
      <Route path="/embed-widget/:username" element={<EmbedWidgetPage />} />
      <Route path="/receipt/:transactionId" element={<ReceiptPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy_policy" element={<PrivacyPolicyPage />} />
      <Route path="/back-us" element={<BackUsPage />} />
      <Route path="/back-us/success" element={<BackUsPage />} />
      <Route path="/roadmap" element={
        <ProtectedRoute>
          <RoadmapPage />
        </ProtectedRoute>
      } />
      
      {/* Auth Routes (redirect if logged in) */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/workspace" element={
        <BusinessProtectedRoute>
          <WorkspaceOverviewPage />
        </BusinessProtectedRoute>
      } />
      <Route path="/app/:section" element={
        <BusinessProtectedRoute>
          <WorkspaceSectionPage />
        </BusinessProtectedRoute>
      } />
      <Route path="/send" element={
        <ProtectedRoute>
          <SendMoneyPage />
        </ProtectedRoute>
      } />
      <Route path="/request" element={
        <ProtectedRoute>
          <RequestMoneyPage />
        </ProtectedRoute>
      } />
      <Route path="/requests" element={
        <ProtectedRoute>
          <RequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <WalletPage />
        </ProtectedRoute>
      } />
      <Route path="/wallet/topup" element={
        <ProtectedRoute>
          <WalletTopupPage />
        </ProtectedRoute>
      } />
      <Route path="/verify-phone" element={
        <ProtectedRoute>
          <PhoneVerifyPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/wallet/success" element={
        <ProtectedRoute>
          <WalletTopupPage />
        </ProtectedRoute>
      } />
      <Route path="/wallet/withdraw" element={
        <ProtectedRoute>
          <WithdrawPage />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <TransactionHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/products/new" element={
        <ProtectedRoute>
          <ProductFormPage />
        </ProtectedRoute>
      } />
      <Route path="/products/edit/:productId" element={
        <ProtectedRoute>
          <ProductFormPage />
        </ProtectedRoute>
      } />
      <Route path="/favorites" element={
        <ProtectedRoute>
          <FavoritesPage />
        </ProtectedRoute>
      } />
      <Route path="/feed" element={
        <ProtectedRoute>
          <FeedPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/fees" element={
        <ProtectedRoute>
          <AdminFeesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/withdrawals" element={
        <ProtectedRoute>
          <AdminWithdrawalsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute>
          <AdminSecurityPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/2fa" element={
        <ProtectedRoute>
          <TwoFAPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/privacy" element={
        <ProtectedRoute>
          <PrivacySettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/invite" element={
        <ProtectedRoute>
          <InviteFriendsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute>
          <AdminFeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/subscriptions" element={
        <ProtectedRoute>
          <MySubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute>
          <MyLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/investor" element={
        <ProtectedRoute>
          <InvestorDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/seller-analytics" element={
        <ProtectedRoute>
          <SellerAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/crm" element={
        <ProtectedRoute>
          <CRMPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <BuyerMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/domains" element={
        <ProtectedRoute>
          <AdminDomainsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/api" element={
        <ProtectedRoute>
          <AdminAPIPage />
        </ProtectedRoute>
      } />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/pledge/:pageId" element={<PublicPledgePage />} />
      <Route path="/biz/login" element={<BusinessLoginPage />} />
      <Route path="/biz/auth/callback" element={<BusinessAuthCallback />} />
      <Route path="/biz/orgs" element={<BusinessProtectedRoute requireOrg={false}><OrgSwitcherPage /></BusinessProtectedRoute>} />
      <Route path="/biz/mfa/enroll" element={<BusinessProtectedRoute requireOrg={false}><MfaEnrollPage /></BusinessProtectedRoute>} />
      <Route path="/biz/setup" element={<BusinessProtectedRoute><BizSetupPage /></BusinessProtectedRoute>} />
      <Route path="/biz/invoices" element={<BusinessProtectedRoute><BizInvoicesPage /></BusinessProtectedRoute>} />
      <Route path="/biz/splits" element={<BusinessProtectedRoute><BizSplitsPage /></BusinessProtectedRoute>} />
      <Route path="/biz/pledges" element={<BusinessProtectedRoute><BizPledgesPage /></BusinessProtectedRoute>} />
      <Route path="/dashboard/unified" element={
        <ProtectedRoute>
          <UnifiedDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/waitlist" element={
        <ProtectedRoute>
          <AdminWaitlistPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/features" element={
        <ProtectedRoute>
          <AdminFeaturesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/beta" element={
        <ProtectedRoute>
          <AdminBetaPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all — username profile route. Must be last so all other routes match first.
          React Router v6 prefers exact paths over dynamic ones, so /login, /explore,
          /admin/*, etc. always match before /:username. */}
      <Route path="/:username" element={<UserProfilePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const INDEXABLE_PATHS = ['/', '/privacy_policy', '/terms', '/terms-of-service'];

function RobotsMeta() {
  const location = useLocation();
  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    if (!INDEXABLE_PATHS.includes(location.pathname)) {
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = 'robots';
        document.head.appendChild(tag);
      }
      tag.content = 'noindex, nofollow';
    } else if (tag) {
      tag.remove();
    }
  }, [location.pathname]);
  return null;
}

function GlobalWidgets() {
  const location = useLocation();
  if (location.pathname === '/') return null; // waitlist landing: no app banners/widgets
  return (
    <>
      <FeedbackWidget />
      <CookieBanner />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessAuthProvider>
          <FeatureFlagsProvider>
            <ThemeProvider>
              <PasscodeGate>
                <AppRouter />
              </PasscodeGate>
              <Toaster position="top-right" richColors />
              <RobotsMeta />
              <GlobalWidgets />
            </ThemeProvider>
          </FeatureFlagsProvider>
        </BusinessAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
